import {
    Add,
    ArchiveOutlined,
    Delete,
    Edit,
    MoreVert,
    Print,
    Search,
    ShoppingCartCheckout,
    UnarchiveOutlined,
} from "@mui/icons-material";
import {
    Box,
    Breadcrumbs,
    Button,
    FormControl,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import api from '../../api';
import ActionBubbleTooltip from '../../components/ActionBubbleTooltip';
import CustomerDisplayName from '../../components/CustomerDisplayName';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import QuotaLimitedButton from '../../components/subscription/QuotaLimitedButton';
import PageTutorialButtons from '../../components/tutorials/PageTutorialButtons';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate } from '../../helpers/formatters';
import {
    QUOTE_STATUSES,
    QUOTE_STATUS_TRANSITIONS,
    quoteCanArchive,
    quoteCanConvert,
    quoteEditable,
    quoteStatusLabel,
} from '../../helpers/quotes';
import { useListFetch } from '../../hooks/useListFetch';

function QuotesPage() {
    const [quotes, setQuotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const navigate = useNavigate();
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
    const [statusMenuQuote, setStatusMenuQuote] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchQuotes = useCallback(() => {
        setIsLoading(true);
        api.get(
            `quotes?page=${currentPage}&limit=${itemsPerPage}&q=${encodeURIComponent(searchTerm)}&status=${encodeURIComponent(statusFilter)}&archived=${showArchived ? 'true' : ''}`,
        )
            .then((response) => {
                setQuotes(response.data.quotes || []);
                setTotalPages(response.data.total_pages || 1);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [currentPage, itemsPerPage, searchTerm, statusFilter, showArchived]);

    useListFetch(fetchQuotes, [currentPage, itemsPerPage, appliedSearchTerm, statusFilter, showArchived, fetchQuotes]);

    const handleSearch = () => {
        setCurrentPage(1);
        setAppliedSearchTerm(true);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleCreateQuote = () => {
        navigate('/admin/register-quote');
    };

    const handleEditQuote = (id) => {
        navigate(`/admin/edit-quote/${id}`);
    };

    const handlePrintQuote = (id) => {
        window.open(`/admin/quotes/${id}/print`, '_blank');
    };

    const handleStatusChange = (quoteId, status) => {
        setStatusMenuAnchor(null);
        setStatusMenuQuote(null);
        api.post(`quotes/${quoteId}/status`, { status })
            .then(() => {
                fetchQuotes();
                Swal.fire('Atualizado!', 'Status do orçamento atualizado.', 'success');
            })
            .catch(() => {
                Swal.fire('Erro!', 'Não foi possível alterar o status.', 'error');
            });
    };

    const handleConvert = (quoteId) => {
        Swal.fire({
            title: 'Transformar em pedido?',
            text: 'O orçamento aceito será convertido em um pedido para você revisar e salvar.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Transformar em pedido',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            api.post(`quotes/${quoteId}/convert-to-checkout`, { shipping_option_index: 0 })
                .then((response) => {
                    const orderUuid = response.data.order_uuid
                        || response.data.checkout_uuid
                        || response.data.data?.order_uuid
                        || response.data.data?.checkout_uuid
                        || '';
                    if (orderUuid) {
                        navigate(`/admin/orders/${orderUuid}/edit`);
                        return;
                    }
                    Swal.fire('Erro!', 'Pedido criado, mas o identificador não foi retornado.', 'error');
                    fetchQuotes();
                })
                .catch(() => {
                    Swal.fire('Erro!', 'Não foi possível transformar o orçamento em pedido.', 'error');
                });
        });
    };

    const handleArchive = (quoteId) => {
        Swal.fire({
            title: 'Arquivar orçamento?',
            text: 'Ele sairá da listagem ativa e poderá ser restaurado depois.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Arquivar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            api.post(`quotes/${quoteId}/archive`)
                .then(() => {
                    fetchQuotes();
                    Swal.fire('Arquivado!', 'O orçamento foi arquivado.', 'success');
                })
                .catch(() => {
                    Swal.fire('Erro!', 'Não foi possível arquivar o orçamento.', 'error');
                });
        });
    };

    const handleUnarchive = (quoteId) => {
        api.post(`quotes/${quoteId}/unarchive`)
            .then(() => {
                fetchQuotes();
                Swal.fire('Restaurado!', 'O orçamento foi desarquivado.', 'success');
            })
            .catch(() => {
                Swal.fire('Erro!', 'Não foi possível desarquivar o orçamento.', 'error');
            });
    };

    const handleDeleteQuote = (quoteId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Este orçamento será removido permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete,
            preConfirm: () => {
                if (loadingDelete) {
                    return;
                }
                setLoadingDelete(true);
                return api.delete(`quotes/${quoteId}`)
                    .then(() => {
                        setLoadingDelete(false);
                        setQuotes((prev) => prev.filter((quote) => quote.id !== quoteId));
                        Swal.fire('Removido!', 'O orçamento foi removido com sucesso.', 'success');
                    })
                    .catch(() => {
                        setLoadingDelete(false);
                        Swal.fire('Erro!', 'Houve um problema ao remover o orçamento.', 'error');
                    });
            },
        });
    };

    const openStatusMenu = (event, quote) => {
        setStatusMenuAnchor(event.currentTarget);
        setStatusMenuQuote(quote);
    };

    const closeStatusMenu = () => {
        setStatusMenuAnchor(null);
        setStatusMenuQuote(null);
    };

    const statusTransitions = statusMenuQuote
        ? (QUOTE_STATUS_TRANSITIONS[statusMenuQuote.status] || [])
        : [];

    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Typography color="text.primary">Orçamentos</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0,
            }}>
                <Typography variant="h4" gutterBottom>
                    Orçamentos
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar por cliente"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 180 }}>
                        <InputLabel id="quote-status-filter-label">Status</InputLabel>
                        <Select
                            labelId="quote-status-filter-label"
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {QUOTE_STATUSES.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>

                <Box sx={{
                    display: 'flex',
                    justifyContent: isMobile ? 'center' : 'flex-end',
                    alignItems: 'center',
                    gap: 2,
                    flexDirection: isMobile ? 'column' : 'row',
                }}>
                    <PageTutorialButtons pageKey="quotes" fullWidth={isMobile} />
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={showArchived ? <UnarchiveOutlined /> : <ArchiveOutlined />}
                        onClick={() => {
                            setShowArchived((prev) => !prev);
                            setCurrentPage(1);
                        }}
                        fullWidth={isMobile}
                    >
                        {showArchived ? 'Ver ativos' : 'Arquivados'}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateQuote}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Novo orçamento
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Validade</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Criado</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(quotes) && quotes.length > 0 ? quotes.map((quote) => {
                            const quoteId = quote.id || quote.uuid;
                            const transitions = QUOTE_STATUS_TRANSITIONS[quote.status] || [];
                            return (
                                <TableRow key={quoteId}>
                                    <TableCell>
                                        <CustomerDisplayName
                                            name={quote.customer_name}
                                            category={quote.customer_category}
                                            fallback="N/A"
                                        />
                                    </TableCell>
                                    <TableCell>{quoteStatusLabel(quote.status)}</TableCell>
                                    <TableCell>{formatCurrencyInCents(quote.amount)}</TableCell>
                                    <TableCell>
                                        {quote.valid_until
                                            ? formatDate(quote.valid_until, false)
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {quote.created_at ? formatDate(quote.created_at) : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {quoteEditable(quote.status) && (
                                            <ActionBubbleTooltip title="Editar">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleEditQuote(quoteId)}
                                                    aria-label="editar orçamento"
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </ActionBubbleTooltip>
                                        )}
                                        <ActionBubbleTooltip title="Imprimir">
                                            <IconButton
                                                color="default"
                                                onClick={() => handlePrintQuote(quoteId)}
                                                aria-label="imprimir orçamento"
                                            >
                                                <Print />
                                            </IconButton>
                                        </ActionBubbleTooltip>
                                        {!showArchived && transitions.length > 0 && (
                                            <ActionBubbleTooltip title="Alterar status">
                                                <IconButton
                                                    color="secondary"
                                                    onClick={(e) => openStatusMenu(e, quote)}
                                                    aria-label="alterar status"
                                                >
                                                    <MoreVert />
                                                </IconButton>
                                            </ActionBubbleTooltip>
                                        )}
                                        {quoteCanConvert(quote.status) && (
                                            <ActionBubbleTooltip title="Transformar em pedido">
                                                <span>
                                                    <QuotaLimitedButton
                                                        quotaKey="quotes"
                                                        size="small"
                                                        variant="text"
                                                        color="success"
                                                        onClick={() => handleConvert(quoteId)}
                                                        aria-label="Transformar em pedido"
                                                        sx={{ minWidth: 40, px: 1 }}
                                                    >
                                                        <ShoppingCartCheckout fontSize="small" />
                                                    </QuotaLimitedButton>
                                                </span>
                                            </ActionBubbleTooltip>
                                        )}
                                        {!showArchived && quoteCanArchive(quote.status) && (
                                            <ActionBubbleTooltip title="Arquivar">
                                                <span>
                                                    <QuotaLimitedButton
                                                        quotaKey="quotes"
                                                        size="small"
                                                        variant="text"
                                                        color="inherit"
                                                        onClick={() => handleArchive(quoteId)}
                                                        aria-label="Arquivar orçamento"
                                                        sx={{ minWidth: 40, px: 1 }}
                                                    >
                                                        <ArchiveOutlined fontSize="small" />
                                                    </QuotaLimitedButton>
                                                </span>
                                            </ActionBubbleTooltip>
                                        )}
                                        {showArchived && (
                                            <ActionBubbleTooltip title="Desarquivar">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleUnarchive(quoteId)}
                                                    aria-label="desarquivar orçamento"
                                                >
                                                    <UnarchiveOutlined />
                                                </IconButton>
                                            </ActionBubbleTooltip>
                                        )}
                                        <ActionBubbleTooltip title="Remover">
                                            <IconButton
                                                color="error"
                                                onClick={() => handleDeleteQuote(quoteId)}
                                                aria-label="remover orçamento"
                                            >
                                                <Delete />
                                            </IconButton>
                                        </ActionBubbleTooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        }) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    Nenhum orçamento encontrado.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={statusMenuAnchor}
                open={Boolean(statusMenuAnchor)}
                onClose={closeStatusMenu}
            >
                {statusTransitions.map((status) => (
                    <MenuItem
                        key={status}
                        onClick={() => handleStatusChange(statusMenuQuote.id || statusMenuQuote.uuid, status)}
                    >
                        {quoteStatusLabel(status)}
                    </MenuItem>
                ))}
            </Menu>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mt: 3,
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        setItemsPerPage(event.target.value);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default QuotesPage;
